type ScrollDirection = 'all' | 'x' | 'y';
namespace pixi_ui.component {
    import InteractionData = PIXI.interaction.InteractionData;
    import InteractionEvent = PIXI.interaction.InteractionEvent;
    import Point = PIXI.Point;
    import ObservablePoint = PIXI.ObservablePoint;

    export class Scroll implements IComponent {
        private interactionData: InteractionData;
        private element: Element;
        private scrollStartPosition: Point;
        private scrollPosition: ObservablePoint;
        private dragStartPosition: Point;
        private previousPosition: Point;
        private readonly velocity: Point;
        private readonly scrollDirection: ScrollDirection;
        private readonly friction: number;

        constructor(direction: ScrollDirection, friction: number) {
            this.scrollDirection = direction;
            this.friction = friction;
            this.velocity = new Point(0, 0);
            this.scrollPosition = new ObservablePoint(
                this.setScrollPosition,
                this
            );
        }

        public attach(element: Element): void {
            this.element = element;
            this.element.interactive = true;
            this.update = this.update.bind(this);
            this.element.updates.push(this.update);

            element.on('pointerdown', this.dragStart, this);
            element.on('pointerup', this.dragEnd, this);
            element.on('pointerupoutside', this.dragEnd, this);
            element.on('pointermove', this.dragMove, this);
        }

        public detach(element: Element): void {
            this.element.updates.splice(
                this.element.updates.indexOf(this.update),
                1
            );
            this.element.interactive = false;
            this.element = null;

            element.off('pointerdown', this.dragStart, this);
            element.off('pointerup', this.dragEnd, this);
            element.off('pointerupoutside', this.dragEnd, this);
            element.off('pointermove', this.dragMove, this);
        }

        private dragStart(e: InteractionEvent) {
            // Store a reference to interactionData so we only
            // capture the first touch when multi-touch is possible.
            this.interactionData = e.data;

            this.dragStartPosition = this.interactionData.getLocalPosition(
                this.element.parent
            );

            this.scrollStartPosition = this.scrollPosition.clone();
        }

        private dragMove(e: InteractionData) {
            if (!this.interactionData) return;

            const position = this.interactionData.getLocalPosition(
                this.element.parent
            );

            if (this.previousPosition) {
                this.velocity.set(
                    (position.x - this.previousPosition.x) * 0.7,
                    (position.y - this.previousPosition.y) * 0.7
                );
            }

            this.previousPosition = position;

            const delta = new Point(
                position.x - this.dragStartPosition.x,
                position.y - this.dragStartPosition.y
            );

            if (this.scrollDirection === 'x') delta.y = 0;
            if (this.scrollDirection === 'y') delta.x = 0;

            this.scrollPosition.set(
                this.scrollStartPosition.x + delta.x,
                this.scrollStartPosition.y + delta.y
            );
        }

        private setScrollPosition() {
            this.scrollPosition.x = Math.max(
                -this.element.contentWidth + this.element.elementWidth,
                Math.min(0, this.scrollPosition.x)
            );

            this.scrollPosition.y = Math.max(
                -this.element.contentHeight + this.element.elementHeight,
                Math.min(0, this.scrollPosition.y)
            );

            this.element.children.forEach(c => {
                if (!(c instanceof Element)) return;
                c.setPosition();
                c.position.x += this.scrollPosition.x;
                c.position.y += this.scrollPosition.y;
            });
        }

        private dragEnd(e: InteractionData) {
            if (!this.interactionData) return;
            this.interactionData = null;
            this.previousPosition = null;
        }

        public resize(element: Element): void {
            this.setScrollPosition();
        }

        public getScrollPosition() {
            return this.scrollPosition;
        }

        public update(delta: number): void {
            if (!!this.interactionData) return;

            if (this.scrollDirection === 'x') this.velocity.y = 0;
            if (this.scrollDirection === 'y') this.velocity.x = 0;

            if (
                Math.abs(this.velocity.x) < 0.0025 &&
                Math.abs(this.velocity.y) < 0.0025
            ) {
                this.velocity.x = 0;
                this.velocity.y = 0;
                return;
            }

            this.velocity.x *= (1 - this.friction) * 0.7 + this.friction;
            this.velocity.y *= (1 - this.friction) * 0.7 + this.friction;

            this.scrollPosition.set(
                this.scrollPosition.x + this.velocity.x,
                this.scrollPosition.y + this.velocity.y
            );
        }
    }
}
